/**
 * <buy-now-price> — shows the selected variant's price on the dynamic checkout
 * button.
 *
 * The button is Shopify's own ({{ form | payment_button }}); its label and its
 * markup belong to Shopify, so nothing inside it can be edited from the theme.
 * The price is therefore added as a CSS pseudo-element whose text comes from a
 * custom property this element keeps up to date -- the payment button's DOM,
 * its listeners and the accelerated-checkout flow are left exactly as Shopify
 * shipped them.
 *
 * Prices are rendered server-side into a JSON block rather than scraped from a
 * price element: the price block is optional on this section (the title block
 * can carry it, or neither can, as on the option-cards layout) so there is not
 * always one to read.
 *
 * The selected variant comes from the form's own hidden input, which
 * global.js:1425 both writes and fires a bubbling `change` on, so one delegated
 * listener covers every way the variant can change.
 *
 * Markup contract:
 *   <buy-now-price data-separator="–">
 *     <script type="application/json" data-variant-prices>{"123":"₹ 799"}</script>
 *     … {% render 'buy-buttons' %} …
 *   </buy-now-price>
 */
(function () {
  class BuyNowPrice extends HTMLElement {
    connectedCallback() {
      this.prices = this.readPrices();
      this.input = this.querySelector('input[name="id"]');
      if (!this.prices || !this.input) return;

      this.onChange = this.onChange.bind(this);
      this.addEventListener('change', this.onChange);
      this.render();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
    }

    readPrices() {
      const source = this.querySelector('[data-variant-prices]');
      if (!source) return null;

      try {
        return JSON.parse(source.textContent);
      } catch {
        return null;
      }
    }

    onChange(event) {
      if (event.target === this.input) this.render();
    }

    render() {
      const price = this.prices[this.input.value];

      if (!price) {
        this.style.removeProperty('--buy-now-price');
        return;
      }

      const separator = this.dataset.separator || '–';
      // A CSS content value must be a quoted string, so the quotes are part of
      // the property -- a quote or backslash in the price would close or escape
      // it early, so they come out first.
      const label = `${separator} ${price}`.replace(/["\\]/g, '');
      this.style.setProperty('--buy-now-price', `"${label}"`);
    }
  }

  if (!customElements.get('buy-now-price')) {
    customElements.define('buy-now-price', BuyNowPrice);
  }
})();
